<template>
  <div class="product-info">
    <!-- Add to locker for hero page product -->
    <div class="add-to-locker-wrapper" v-if="showAddToLockerButton">
      <AddToLockerButton
        v-if="showAddToLockerButton && !anonymousCrossOrigin"
        :isProjectLocked="isProjectLocked"
        :dealerMode="dealerMode"
        @addToLocker="handleAddToLocker"
      />
    </div>

    <ModalAlert
      v-if="showAlertModal"
      :text="'Error trying to save the product in your locker'"
      :title="'Error'"
      :isError="true"
      @closeModal="handleCloseAlertModal"
    />

    <!-- Title -->
    <div class="featured-title" v-if="isFeatured">Featured Items</div>
    <div class="grid-title-container">
      <!-- Brand -->
      <div class="branding" v-if="withBranding">
        <ProductBrandIcon :iconName="brandName" />
      </div>

      <div class="gender-selection">
        <!-- <span class="slate-bold dark-grey">Available in</span> -->
        <div class="genders">
          <button
            type="button"
            :class="{ active: row.gender === activeGender }"
            v-for="(row, i) in rows"
            :key="row.alias + ' ' + i"
            @click="$emit('setActiveGender', row.gender)"
          >
            {{ row.gender }}
          </button>
        </div>
      </div>

      <!-- Custom catalog style id -->
      <p
        class="grid-title slate-bold"
        :class="{ 'with-background': pageType === 'Designs' }"
      >
        {{ name }}
      </p>

      <!-- Showroom price -->
      <p v-if="showShowroomPrice" class="product-price slate">
        ${{ productPrice }}
      </p>
    </div>

    <!-- Prices -->
    <ul class="grid-container" v-if="showProductInfo">
      <GridRow
        :row="activeGridRow"
        :index="itemIndex"
        :product="product"
        :relatedRows="rows"
        :productIsEditable="productIsEditable && !anonymousCrossOrigin"
        :loading="loading"
        :isPlaceholderPage="isPlaceholderPage"
        :pageId="pageId"
        :columnIndex="columnIndex"
        :itemIndex="itemIndex"
        :sportName="sportName"
        :layoutIndex="layoutIndex"
        :hasManyRows="hasManyRows"
        :showPrice="showPrice"
      />

      <div
        v-if="isPlaceholderPage && showPrice"
        class="edit-item-indicator right"
      >
        Edit prices
      </div>
    </ul>

    <!-- Description -->
    <ul
      class="product-description slate-light product-description-list"
      v-if="withDescription && !descriptionEditable && productDescriptionView"
    >
      <li
        v-for="(item, index) in productDescriptionView"
        :key="index + '-' + product.id"
      >
        <span>
          {{ item }}
        </span>
      </li>
    </ul>
    <div
      class="product-description slate-light product-description-editable"
      v-if="withDescription && descriptionEditable"
    >
      <textarea
        class="slate-light"
        rows="4"
        resize="none"
        v-model="productDescription"
        @input="
          () => {
            debounceUpdate({
              field: 'description',
            });
          }
        "
      >
      </textarea>
    </div>
  </div>
</template>

<script>
import CONSTANTS from "@/constants";

import { debounce } from "debounce";

import AddToLockerButton from "@/components/Projects/Snapsheets/AddToLockerButton.vue";
import ProductBrandIcon from "@/components/Projects/Product/ProductBrandIcon.vue";
import ModalAlert from "@/components/Utils/ModalAlert.vue";

import GridRow from "@/components/Projects/Product/GridRow.vue";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

export default {
  name: "ProductInfo",

  data() {
    return {
      loading: false,
      showAlertModal: false,
      waiting: false,
    };
  },

  methods: {
    ...mapMutations("project", ["TOGGLE_LOCKER_PREVIEW_MODAL"]),
    ...mapActions("playbook", ["updatePlaybookItem"]),
    ...mapActions("project", [
      "triggerLoading",
      "endLoading",
      "showSaveIndicator",
      "addToLocker",
      "updateProjectStatus",
    ]),

    handleCloseAlertModal() {
      this.showAlertModal = false;
    },

    async handleAddToLocker() {
      this.loading = true;
      try {
        //Layout-> for sublimables
        await this.addToLocker({
          product: this.product,
          layout: this.finalLayout,
          isPrecolored:
            !!this.precoloredDesignShadow || !!this.precoloredDesign,
          precoloredDesign: this.precoloredDesign,
          precoloredShadow: this.precoloredDesignShadow,
          isChromagear: this.isChromagear,
          designDimension: this.designDimension,
          heroDesign: this.heroDesign,
        });

        //Update project status to 'Open Locker'
        await this.updateProjectStatus(CONSTANTS.PROJECT_STATUS.OPEN_LOCKER);

        //this.wasAddedToLocker = true;
        this.TOGGLE_LOCKER_PREVIEW_MODAL();
      } catch (error) {
        console.log(error);
        this.showAlertModal = true;
      } finally {
        this.loading = false;
      }
    },

    debounceUpdate: debounce(async function ({ field }) {
      //save to backend
      const sportSlug = this.getPlaybookCards.find((s) => {
        return s.name.toLowerCase() === this.sportName.toLowerCase();
      }).slug;

      try {
        this.triggerLoading();
        this.showSaveIndicator();

        await this.updatePlaybookItem({
          layout: this.layoutIndex,
          column: this.columnIndex,
          block: this.itemIndex,
          [field]: this.productDescription,
          sport: sportSlug,
        });
      } catch (error) {
        console.log(error);
      } finally {
        this.endLoading();
      }
    }, 500),
  },

  computed: {
    ...mapGetters("project", [
      "isSnapsheet",
      "isPlaybook",
      "isShowroom",
      "isEditMode",
    ]),
    ...mapState("auth", ["dealerMode"]),
    ...mapState("snapsheet", ["showPrices"]),
    ...mapState(["dealer"]),
    ...mapGetters("playbook", ["getPlaybookCards"]),
    ...mapGetters("actionsPermissions", ["isProjectLocked"]),

    brandName() {
      return this.product?.brand;
    },

    hasManyRows() {
      return this.rows?.length > 2;
    },

    name() {
      if (this.nameOverride != null) return this.nameOverride;
      return this.pageType?.toLowerCase() === "designs"
        ? this.product.design.name || this.product.design?.[0]?.design_name
        : this.product.name;
    },

    showAddToLockerButton() {
      //return this.pageType?.toLowerCase() === "hero"
      return false;
    },

    activeGridRow() {
      if (!this.activeGender) return {};

      return this.rows?.find((row) => row?.gender === this.activeGender);
    },

    rows() {
      //Playbooks rows
      const related = this.product ? (this.product.related || this.product.related_data) : null
      if (!related) return [];

      return related?.map((prData) => {
        const gender = prData?.gender;

        const sizesRange = prData?.sizes || "N/A";

        const style = prData?.style_id || prData?.styleId;

        const styleOrAlias =
          style?.[0]?.toLowerCase() === "a" ? prData?.alias : style;

        const prDataPrice = prData?.price;
        const customPrice = prData?.customPrice;
        const dealerPrice = prData?.dealerPrice;

        return {
          styleId: styleOrAlias,
          gender,
          sizes: sizesRange,
          customPrice: customPrice,
          dealerPrice: dealerPrice || null,
          price: prDataPrice,
        };
      });
    },

    descriptionEditable() {
      const tempDisabled = true;
      return (
        !tempDisabled &&
        this.dealerMode &&
        this.isEditMode &&
        this.withDescription &&
        this.isPlaybook
      );
    },

    showProductInfo() {
      if (this.isSnapsheet) return true;
      return this.withProductGrid; //todo designs page limit
    },

    showPrice() {
      if (this.isSnapsheet) {
        return this.showPrices === false
          ? false
          : this.dealer?.settings?.display_msrp;
      }

      return this.dealer?.settings?.display_msrp;
    },

    productPrice() {
      const price =
        this.product.customPrice || this.product.price || this.product.msrp;

      return isNaN(price) || !price ? "No price" : Number(price).toFixed(2);
    },

    productStyleId() {
      return this.product?.styleId || this.product?.style_id;
    },

    productDescriptionMaxBulletPoints() {
      return this.isShowroom ? 2 : 1;
    },

    productDescriptionIsTooLong() {
      return (
        this.productDescription?.length > this.productDescriptionMaxBulletPoints
      );
    },

    productDescription() {
      //Array -> New descriptions
      return this.product?.description;
    },

    productDescriptionView() {
      return !this.productDescriptionIsTooLong || this.showFullDescription
        ? this.productDescription
        : this.productDescription.slice(
            0,
            this.productDescriptionMaxBulletPoints
          );
    },
  },

  props: {
    product: {
      type: Object,
    },
    activeGender: {
      type: String,
    },
    withDescription: {
      type: Boolean,
    },
    withBranding: {
      type: Boolean,
    },
    itemsCount: {
      type: Number,
    },
    isPlaceholderPage: {
      type: Boolean,
    },
    productIsEditable: {
      type: Boolean,
    },
    withProductGrid: {
      type: Boolean,
    },
    pageType: {
      type: String,
    },
    pageId: {
      type: Number,
    },
    columnIndex: {
      type: Number,
    },
    itemIndex: {
      type: Number,
    },
    sportName: {
      type: String,
    },
    layoutIndex: {
      type: Number,
    },
    anonymousCrossOrigin: {
      type: Boolean,
    },
    isFeatured: {
      type: Boolean,
    },
    nameOverride: {
      type: String,
    },
    heroDesign: {
      type: String,
    },
    showShowroomPrice: {
      type: Boolean,
    },
  },

  components: {
    AddToLockerButton,
    GridRow,
    ModalAlert,
    ProductBrandIcon,
  },
};
</script>

<style lang="scss" scoped>
#app {
  .product-info {
    position: relative;
    margin-bottom: 1em;
    height: 40%;
    width: 100%;

    .featured-title {
      font-family: "Forza";
      text-transform: uppercase;
      font-style: italic;
      font-size: 1.4em;
      line-height: 1.4em;
      text-align: left;
      margin: 0.5em 0 1em;
      letter-spacing: 0.05em;
      color: #333;
    }

    .grid-title-container {
      display: flex;
      flex-direction: column;

      .gender-selection {
        display: flex;
        flex-direction: column;
        margin-bottom: 0.5em;
        span {
          margin-right: 1em;
          margin-bottom: 0.5em;
        }

        .genders {
          display: flex;
          button {
            display: flex;
            align-items: center;
            background: $placeholder;
            color: white;
            padding: 0.1em 0.75em;
            margin-right: 0.5em;
            font-size: 0.8em;
            flex: 0;

            &.active {
              background: $dark;
            }
          }
        }
      }
      // align-items: center;
      .product-style-id,
      .product-price {
        color: $grey-dark;
      }
      .product-price {
        font-size: 1.425rem;
        margin-bottom: 0.25rem;
      }
      .grid-title {
        font-weight: bold;
        font-size: 1.1em;
        display: flex;
        align-items: center;
        // margin: 0 auto;
        margin-bottom: 0.8em;
        padding: 0;
        text-align: center;
        text-transform: capitalize;

        &.with-background {
          background: $grey-dark;
          color: white;
          display: flex;
          justify-content: center;
          text-transform: uppercase;
          width: auto;
          margin-left: auto;
          margin-right: auto;
          padding: 0.1em 1em;
        }
      }
    }

    .grid-container {
      display: flex;
      flex-flow: column;
      list-style: none;
      margin: 0;
    }
    .product-description {
      font-size: 0.85em;
      max-height: 6em;
      // overflow: hidden;
      text-overflow: ellipsis;
      color: $dark;
      padding: 0.25em;
      list-style: disc !important;
      &.product-description-list {
        margin-left: 1.2em;
      }
      &.product-description-editable {
        textarea {
          width: 100%;
          padding: 0 0.5em;
          border-color: #afafaf;
        }
      }
    }

    .add-to-locker-wrapper {
      position: absolute;
      right: 0.5em;
      top: -2em;
    }
  }
}
</style>
